import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Search, User, Sparkles, Crown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowseUsersPage = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    checkSubscription();
    fetchUsers();
  }, []);

  const checkSubscription = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/payments/subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setIsPremium(response.data.plan_type === 'premium');
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  };

  const fetchUsers = async (search = '') => {
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const url = search ? `${API}/users/browse?search=${encodeURIComponent(search)}` : `${API}/users/browse`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('This feature is Premium only!');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery);
  };

  if (!isPremium && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-rose-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="shadow-2xl border-amber-200">
            <CardContent className="p-12 text-center">
              <Crown className="w-20 h-20 text-amber-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-rose-900 mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                Premium Feature
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Browse and search all users is exclusive to Premium members!
              </p>
              <Button
                data-testid="upgrade-to-premium-btn"
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white px-8 py-6 text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-rose-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-5xl font-bold text-rose-900" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Browse All Users
            </h1>
            <div className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-rose-400 text-white px-4 py-2 rounded-full">
              <Sparkles className="w-4 h-4" />
              <span className="font-semibold">Premium Feature</span>
            </div>
          </div>
          <p className="text-lg text-gray-600">Search and connect with anyone on the platform</p>
        </div>

        {/* Search Bar */}
        <Card className="shadow-lg border-rose-100 mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-4">
              <Input
                data-testid="search-users-input"
                type="text"
                placeholder="Search by name, location, or bio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <Button
                data-testid="search-users-btn"
                type="submit"
                className="bg-rose-500 hover:bg-rose-600"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              {searchQuery && (
                <Button
                  data-testid="clear-search-btn"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    fetchUsers('');
                  }}
                  className="border-rose-200"
                >
                  Clear
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {users.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <Search className="w-16 h-16 text-rose-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-rose-900 mb-2">No users found</h3>
              <p className="text-gray-600">Try a different search term</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 text-gray-600">
              Found <span className="font-semibold text-rose-700">{users.length}</span> users
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {users.map((browseUser) => (
                <Card
                  key={browseUser.id}
                  data-testid={`user-card-${browseUser.id}`}
                  className="overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-rose-100"
                >
                  <div className="h-64 bg-gradient-to-br from-rose-200 to-amber-200 flex items-center justify-center">
                    {browseUser.picture ? (
                      <img src={browseUser.picture} alt={browseUser.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-24 h-24 text-rose-400" />
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                      {browseUser.name}
                      {browseUser.date_of_birth && `, ${new Date().getFullYear() - new Date(browseUser.date_of_birth).getFullYear()}`}
                    </CardTitle>
                    <CardDescription>
                      {browseUser.location?.city && `${browseUser.location.city}, `}
                      {browseUser.location?.country}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {browseUser.bio && (
                      <p className="text-sm text-gray-600 line-clamp-3 mb-4">{browseUser.bio}</p>
                    )}
                    
                    {browseUser.core_values?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-rose-900 mb-2">Values:</p>
                        <div className="flex flex-wrap gap-1">
                          {browseUser.core_values.slice(0, 3).map((value) => (
                            <span key={value} className="px-2 py-1 bg-rose-100 text-rose-700 rounded-full text-xs">
                              {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      data-testid={`view-profile-btn-${browseUser.id}`}
                      onClick={() => navigate(`/profile/${browseUser.id}`)}
                      className="w-full bg-rose-500 hover:bg-rose-600"
                      size="sm"
                    >
                      View Full Profile
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BrowseUsersPage;
